import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PartyPopper, Heart, Star, Coffee, Bath, Footprints, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function WeeklyCelebrationGuide() {
  const navigate = useNavigate();

  const celebrationIdeas = [
    { icon: Coffee, title: "Favorite Coffee Date", desc: "Visit your favorite café and savor your drink slowly" },
    { icon: Bath, title: "Spa Night In", desc: "Face mask, bubble bath, candles, and relaxing music" },
    { icon: Footprints, title: "Nature Walk", desc: "30 minutes in your favorite park or trail" },
    { icon: Star, title: "Buy Yourself Flowers", desc: "A small bouquet to brighten your space" },
    { icon: Heart, title: "Journal & Reflect", desc: "Write a love letter to yourself about your progress" },
    { icon: PartyPopper, title: "Treat Yourself", desc: "A small gift—book, accessory, or something you've wanted" }
  ];

  const reflectionPrompts = [
    "What task was hardest this week, and how did I handle it?",
    "What surprised me about myself this week?",
    "What new insight did I gain?",
    "How did I show up for myself?",
    "What would I tell a friend who accomplished what I did?"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
            Week 1 • Day 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Weekly Celebration
          </h1>
          <p className="text-xl text-secondary-600">Honor Your Progress and Build Momentum</p>
        </div>

        {/* Celebration Banner */}
        <section className="bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <PartyPopper className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">You Made It Through Week 1!</h3>
              <p className="text-amber-100 text-lg">
                This is not a small thing. You showed up for yourself every single day. That deserves recognition.
              </p>
            </div>
          </div>
        </section>

        {/* Why Celebrate */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Celebration Matters</h2>
          <p className="text-secondary-700 mb-4">
            Many women skip this step—we're trained to minimize our achievements and immediately move to the next goal. 
            But celebration is scientifically crucial for lasting change:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Dopamine Release", desc: "Celebrating triggers the brain's reward system, making you more likely to repeat positive behaviors" },
              { title: "Builds Self-Trust", desc: "Acknowledging wins proves to yourself that you can follow through" },
              { title: "Creates Momentum", desc: "Positive emotions fuel continued effort and make the next week easier" },
              { title: "Fights Perfectionism", desc: "Celebrating progress (not perfection) builds a healthier mindset" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-amber-50 rounded-xl">
                <h3 className="font-bold text-amber-700 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Today's Practice */}
        <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Today's Practice</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="font-bold text-secondary-900">Choose Your Mini Celebration</h3>
                <p className="text-secondary-600">Pick something from the ideas below or create your own. This should feel like a treat, not a task.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="font-bold text-secondary-900">While Enjoying It, Reflect</h3>
                <p className="text-secondary-600">Mentally review 3 things you did well this week. Say them out loud or write them down.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="font-bold text-secondary-900">Acknowledge Your Progress</h3>
                <p className="text-secondary-600">Look yourself in the mirror and say: "I'm proud of myself for showing up this week."</p>
              </div>
            </div>
          </div>
        </section>

        {/* Celebration Ideas */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Celebration Ideas</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {celebrationIdeas.map((idea, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                <idea.icon className="w-8 h-8 text-amber-500 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-secondary-900">{idea.title}</h3>
                  <p className="text-sm text-secondary-600">{idea.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reflection Prompts */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Weekly Reflection Prompts</h2>
          <p className="text-secondary-600 mb-4">Take a few minutes to consider these questions:</p>
          <div className="space-y-3">
            {reflectionPrompts.map((prompt, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <span className="text-secondary-700">{prompt}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default WeeklyCelebrationGuide;
